import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { useAtomValue } from 'jotai'
import { Eye, HelpCircle, Music, Plus, Search } from 'lucide-react'
import './Scores.scss'
import Sidebar from '../../components/Sidebar/Sidebar'
import MainURL from '../../MainURL'
import { drumtaleAdminSessionAtom, drumtaleAdminTokenAtom } from '../../AtomStore'
import { gradeAccessPolicy, openPageSpecification } from '../shared/pageSpecification'
import {
  Score,
  difficultyLabels,
  scoreCategories,
  scoresDataModelGuide,
  scoresImplementationGuide,
  scoresSpecification,
  scoresStateDefinitions,
} from './scoresData'

const TOAST_DURATION_MS = 2400

type PeriodMode = 'day' | 'week' | 'month'
type SortMode = 'popular' | 'latest' | 'oldest'
type VisibilityFilter = '' | 'public' | 'private'
type FreeFilter = '' | 'free' | 'paid'

function toLocalDateText(date: Date) {
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

function matchesPeriod(value: string, start: string, end: string) {
  if (!value || value === '-') return true
  const dateText = value.slice(0, 10)
  return dateText >= (start || '1900-01-01') && dateText <= (end || '2999-12-31')
}

function Scores() {
  const navigate = useNavigate()
  const location = useLocation()
  const isAdminSessionActive = useAtomValue(drumtaleAdminSessionAtom)
  const adminToken = useAtomValue(drumtaleAdminTokenAtom)

  const [scoreList, setScoreList] = useState<Score[]>([])
  const [isLoadingScores, setIsLoadingScores] = useState(true)
  const [search, setSearch] = useState('')
  const [genre, setGenre] = useState('')
  const [sort, setSort] = useState<SortMode>('latest')
  const [visibility, setVisibility] = useState<VisibilityFilter>('')
  const [free, setFree] = useState<FreeFilter>('')
  const [periodStart, setPeriodStart] = useState(() => {
    const now = new Date()
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-01`
  })
  const [periodEnd, setPeriodEnd] = useState(() => toLocalDateText(new Date()))
  const [selectedTitles, setSelectedTitles] = useState<Set<string>>(new Set())

  const [toastMessage, setToastMessage] = useState('')
  const [isToastVisible, setIsToastVisible] = useState(false)
  const toastTimerRef = useRef<number | null>(null)

  useEffect(() => {
    if (!isAdminSessionActive) {
      navigate('/')
    }
  }, [isAdminSessionActive, navigate])

  useEffect(() => {
    return () => {
      if (toastTimerRef.current) {
        window.clearTimeout(toastTimerRef.current)
      }
    }
  }, [])

  const showToast = (message: string) => {
    if (toastTimerRef.current) {
      window.clearTimeout(toastTimerRef.current)
    }
    setToastMessage(message)
    setIsToastVisible(true)
    toastTimerRef.current = window.setTimeout(() => setIsToastVisible(false), TOAST_DURATION_MS)
  }

  useEffect(() => {
    if (!isAdminSessionActive || !adminToken) {
      setIsLoadingScores(false)
      return
    }

    let cancelled = false
    const pendingScore = (location.state as { newScore?: Score } | null)?.newScore || null
    const sleep = (ms: number) => new Promise((resolve) => window.setTimeout(resolve, ms))

    const loadScores = async () => {
      const url = `${MainURL}/admin/scores`
      const maxAttempts = 5
      setIsLoadingScores(true)

      if (pendingScore) await sleep(800)

      for (let attempt = 1; attempt <= maxAttempts; attempt += 1) {
        if (cancelled) return
        try {
          const response = await fetch(url, {
            headers: { Authorization: `Bearer ${adminToken}` },
          })
          const data = await response.json().catch(() => null)
          if (!response.ok) {
            if (response.status >= 500 && attempt < maxAttempts) {
              await sleep(attempt * 700)
              continue
            }
            if (!cancelled) showToast(data?.message || '악보 목록을 불러오지 못했습니다.')
            return
          }
          if (!cancelled) setScoreList(data?.scores ?? [])
          return
        } catch (error) {
          if (attempt < maxAttempts) {
            await sleep(attempt * 700)
            continue
          }
          if (cancelled) return
          if (pendingScore) {
            setScoreList((previous) => {
              const exists = previous.some((item) => item.scoreId && item.scoreId === pendingScore.scoreId)
              return exists ? previous : [pendingScore, ...previous]
            })
            showToast('목록 불러오기에 실패했습니다. 잠시 후 새로고침해 주세요.')
          } else {
            showToast('악보 목록을 불러오지 못했습니다.')
          }
        }
      }
    }

    loadScores().finally(() => {
      if (!cancelled) setIsLoadingScores(false)
    })

    return () => {
      cancelled = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [adminToken, isAdminSessionActive])

  useEffect(() => {
    const state = location.state as { newScore?: Score, isEdit?: boolean, warning?: string | null } | null
    if (!state?.newScore) return
    showToast(state.warning || (state.isEdit ? '수정되었습니다.' : '저장되었습니다.'))
    navigate(location.pathname, { replace: true, state: null })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state])

  const handlePeriodModeChange = (mode: PeriodMode) => {
    const today = new Date()
    const todayText = toLocalDateText(today)
    if (mode === 'week') {
      const weekAgo = new Date(today)
      weekAgo.setDate(today.getDate() - 6)
      setPeriodStart(toLocalDateText(weekAgo))
      setPeriodEnd(todayText)
    } else if (mode === 'day') {
      setPeriodStart(todayText)
      setPeriodEnd(todayText)
    } else {
      setPeriodStart(`${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-01`)
      setPeriodEnd(todayText)
    }
  }

  const handleOpenSpecification = () => {
    const opened = openPageSpecification(scoresSpecification, scoresDataModelGuide, scoresImplementationGuide, {
      gradeAccessPolicy,
      stateDefinitions: scoresStateDefinitions,
    })
    if (!opened) {
      showToast('화면 사양서 창을 열 수 없습니다. 팝업 차단을 해제해 주세요.')
    }
  }

  const filteredScores = useMemo(() => {
    return scoreList
      .filter((score) => matchesPeriod(score.registered, periodStart, periodEnd))
      .filter((score) => !genre || score.genre === genre)
      .filter((score) => !visibility || (visibility === 'public' ? !score.hidden && score.status === '공개' : score.hidden || score.status === '미공개'))
      .filter((score) => !free || (free === 'free' ? score.freeLevels.length > 0 : score.freeLevels.length === 0))
      .filter((score) => !search || `${score.title} ${score.artist}`.toLowerCase().includes(search.toLowerCase()))
      .slice()
      .sort((a, b) => {
        if (sort === 'latest') return b.updated.localeCompare(a.updated)
        if (sort === 'oldest') return a.updated.localeCompare(b.updated)
        return b.plays - a.plays
      })
  }, [scoreList, search, genre, sort, visibility, free, periodStart, periodEnd])

  const isAllSelected = filteredScores.length > 0 && filteredScores.every((score) => selectedTitles.has(score.title))

  const toggleSelectAll = () => {
    setSelectedTitles((previous) => {
      const next = new Set(previous)
      if (isAllSelected) {
        filteredScores.forEach((score) => next.delete(score.title))
      } else {
        filteredScores.forEach((score) => next.add(score.title))
      }
      return next
    })
  }

  const toggleSelectRow = (title: string) => {
    setSelectedTitles((previous) => {
      const next = new Set(previous)
      if (next.has(title)) {
        next.delete(title)
      } else {
        next.add(title)
      }
      return next
    })
  }

  const toggleHidden = (title: string) => {
    setScoreList((previous) => previous.map((score) => (score.title === title ? { ...score, hidden: !score.hidden } : score)))
  }

  const handleBulkAction = (action: 'free' | 'hidden' | 'delete') => {
    if (selectedTitles.size === 0) {
      showToast('선택된 악보가 없습니다.')
      return
    }
    if (action === 'delete') {
      setScoreList((previous) => previous.filter((score) => !selectedTitles.has(score.title)))
      showToast(`${selectedTitles.size}곡을 삭제했습니다.`)
      setSelectedTitles(new Set())
      return
    }
    if (action === 'hidden') {
      setScoreList((previous) => previous.map((score) => (selectedTitles.has(score.title) ? { ...score, hidden: true } : score)))
      showToast(`${selectedTitles.size}곡을 미공개로 전환했습니다.`)
      return
    }
    showToast('무료 공개 설정 화면은 추후 제공됩니다.')
  }

  const totalPlays = scoreList.reduce((sum, score) => sum + score.plays, 0)

  return (
    <div className='Scores'>
      <div className='app-shell'>
        <Sidebar />

        <main className='workspace'>
          <header className='topbar'>
            <div className='title-area'>
              <h1>악보관리</h1>
            </div>
          </header>

          <section className='view active'>
            <div className='content-header'>
              <div className='button-row'>
                <button className='primary-button' type='button' onClick={() => handleBulkAction('free')}>무료 공개 설정</button>
                <button className='secondary-button' type='button' onClick={() => handleBulkAction('hidden')}>선택 미공개</button>
                <button className='secondary-button danger' type='button' onClick={() => handleBulkAction('delete')}>선택 삭제</button>
                <button className='primary-button' type='button' onClick={() => navigate('/admin/scores/register')}>
                  <Plus size={15} />
                  악보 등록
                </button>
              </div>
            </div>

            <div className='toolbar'>
              <div className='filter-group'>
                <label className='inline-search'>
                  <Search size={15} />
                  <input type='search' placeholder='곡명 또는 가수' value={search} onChange={(event) => setSearch(event.target.value)} />
                </label>
                <div className='period-range'>
                  <select className='filter period-filter' defaultValue='month' onChange={(event) => handlePeriodModeChange(event.target.value as PeriodMode)}>
                    <option value='day'>일간</option>
                    <option value='week'>주간</option>
                    <option value='month'>월간</option>
                  </select>
                  <input className='date-filter' type='date' value={periodStart} onChange={(event) => setPeriodStart(event.target.value)} />
                  <span>~</span>
                  <input className='date-filter' type='date' value={periodEnd} onChange={(event) => setPeriodEnd(event.target.value)} />
                </div>
                <select className='filter' value={genre} onChange={(event) => setGenre(event.target.value)}>
                  <option value=''>장르 전체</option>
                  {scoreCategories.map((category) => (
                    <option key={category} value={category}>{category}</option>
                  ))}
                </select>
                <select className='filter' value={sort} onChange={(event) => setSort(event.target.value as SortMode)}>
                  <option value='popular'>인기순</option>
                  <option value='latest'>최신순</option>
                  <option value='oldest'>오래된순</option>
                </select>
                <select className='filter' value={visibility} onChange={(event) => setVisibility(event.target.value as VisibilityFilter)}>
                  <option value=''>노출 전체</option>
                  <option value='public'>공개</option>
                  <option value='private'>미공개</option>
                </select>
                <select className='filter' value={free} onChange={(event) => setFree(event.target.value as FreeFilter)}>
                  <option value=''>무료 회원 이용 전체</option>
                  <option value='free'>무료 회원 이용 가능</option>
                  <option value='paid'>무료 회원 이용 불가</option>
                </select>
              </div>
            </div>

            <div className='score-workbench'>
              {isLoadingScores ? (
                <>
                  <article className='score-skeleton-card' aria-hidden>
                    <span className='score-skeleton-line short' />
                    <strong className='score-skeleton-line medium' />
                    <small className='score-skeleton-line long' />
                  </article>
                  <article className='score-skeleton-card' aria-hidden>
                    <span className='score-skeleton-line short' />
                    <strong className='score-skeleton-line medium' />
                    <small className='score-skeleton-line long' />
                  </article>
                  <article className='score-skeleton-card' aria-hidden>
                    <span className='score-skeleton-line short' />
                    <strong className='score-skeleton-line medium' />
                    <small className='score-skeleton-line long' />
                  </article>
                  <article className='workbench-action score-skeleton-card' aria-hidden>
                    <span className='score-skeleton-line button' />
                  </article>
                </>
              ) : (
                <>
                  <article>
                    <span>등록 완료</span>
                    <strong>{scoreList.length}곡</strong>
                    <small>Easy, Normal, Hard 모두 등록</small>
                  </article>
                  <article>
                    <span>필수 난이도</span>
                    <strong>{difficultyLabels.length}개</strong>
                    <small>모든 악보에 동일 적용</small>
                  </article>
                  <article>
                    <span>이번 달 플레이</span>
                    <strong>{totalPlays.toLocaleString()}회</strong>
                    <small>등록곡 전체 합산</small>
                  </article>
                  <article className='workbench-action'>
                    <button className='secondary-button' type='button' onClick={() => showToast('사용자 노출 미리보기는 추후 제공됩니다.')}>
                      <Eye size={15} />
                      사용자 노출 미리보기
                    </button>
                  </article>
                </>
              )}
            </div>

            <div className='table-wrap' aria-busy={isLoadingScores}>
              <table>
                <thead>
                  <tr>
                    <th><input type='checkbox' checked={isAllSelected} onChange={toggleSelectAll} disabled={isLoadingScores} /></th>
                    <th>곡</th>
                    <th>장르</th>
                    <th>상태</th>
                    <th>등록 난이도</th>
                    <th>무료 회원 이용</th>
                    <th>등록날짜</th>
                    <th>마지막 플레이</th>
                    <th>이번 달</th>
                    <th>추천</th>
                    <th>미리보기</th>
                    <th>미공개</th>
                    <th>관리</th>
                  </tr>
                </thead>
                <tbody>
                  {isLoadingScores ? (
                    Array.from({ length: 8 }, (_, index) => (
                      <tr key={`skeleton-row-${index}`} className='score-skeleton-row'>
                        <td><span className='score-skeleton-block check' /></td>
                        <td>
                          <div className='song-cell'>
                            <span className='score-skeleton-block thumb' />
                            <div className='score-skeleton-song-text'>
                              <span className='score-skeleton-line medium' />
                              <span className='score-skeleton-line short' />
                            </div>
                          </div>
                        </td>
                        <td><span className='score-skeleton-line short' /></td>
                        <td><span className='score-skeleton-line short' /></td>
                        <td><span className='score-skeleton-line long' /></td>
                        <td><span className='score-skeleton-line medium' /></td>
                        <td><span className='score-skeleton-line short' /></td>
                        <td><span className='score-skeleton-line medium' /></td>
                        <td><span className='score-skeleton-line short' /></td>
                        <td><span className='score-skeleton-line short' /></td>
                        <td><span className='score-skeleton-line short' /></td>
                        <td><span className='score-skeleton-line short' /></td>
                        <td><span className='score-skeleton-line short' /></td>
                      </tr>
                    ))
                  ) : filteredScores.length === 0 ? (
                    <tr><td colSpan={13}>검색 조건에 맞는 데이터가 없습니다.</td></tr>
                  ) : (
                    filteredScores.map((score) => (
                      <tr key={score.scoreId ?? score.title}>
                        <td><input type='checkbox' checked={selectedTitles.has(score.title)} onChange={() => toggleSelectRow(score.title)} /></td>
                        <td>
                          <div className='song-cell'>
                            <span className='thumb'><Music size={16} /></span>
                            <div>
                              <strong>{score.title}</strong>
                              <small>{score.artist}</small>
                            </div>
                          </div>
                        </td>
                        <td>{score.genre}</td>
                        <td><span className={`badge ${score.hidden ? 'warning' : 'success'}`}>{score.hidden ? '미공개' : '공개'}</span></td>
                        <td>
                          <span className='badge primary'>3/3 필수</span> <small>{difficultyLabels.join(', ')}</small>
                        </td>
                        <td>
                          <div className='free-level-cell'>
                            {score.freeLevels.length ? <span className='badge success'>{score.freeLevels.join(', ')}</span> : '-'}
                            <button className='secondary-button' type='button' onClick={() => showToast('무료 공개 난이도 설정은 추후 제공됩니다.')}>설정</button>
                          </div>
                        </td>
                        <td>{score.registered}</td>
                        <td>{score.lastPlayed}</td>
                        <td>{score.plays.toLocaleString()}회</td>
                        <td>{score.featured}</td>
                        <td>
                          <button className='preview-button' type='button' onClick={() => showToast(`${score.title} 악보 미리보기를 열었습니다.`)}>
                            <Eye size={15} />
                            보기
                          </button>
                        </td>
                        <td>
                          <label className='toggle'>
                            <input type='checkbox' checked={score.hidden} onChange={() => toggleHidden(score.title)} />
                            미공개
                          </label>
                        </td>
                        <td>
                          <button
                            className='secondary-button'
                            type='button'
                            onClick={() => {
                              if (!score.scoreId) {
                                showToast('수정할 악보를 찾을 수 없습니다.')
                                return
                              }
                              navigate(`/admin/scores/${score.scoreId}/edit`)
                            }}
                          >
                            수정
                          </button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </section>
        </main>
      </div>

      <button
        className='page-spec-button'
        type='button'
        aria-label='현재 화면 사양서 열기'
        title='현재 화면 사양서'
        onClick={handleOpenSpecification}
      >
        <HelpCircle size={22} />
      </button>

      <div className={`toast${isToastVisible ? ' show' : ''}`} role='status' aria-live='polite'>
        {toastMessage}
      </div>
    </div>
  )
}

export default Scores
